import React, { memo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CFormInput, CInputGroup } from '@coreui/react-pro';
import classNames from 'classnames';

import { cloneDeep, removeTrailingSlash, updateNodeState } from '../../../../../utils';
import { DEFAULT_FN, POPUP_TYPE } from '../../../../../constants';

import SellingGroupFilterChoose from './SellingGroupFilterChoose';
import SellingGroupFilterPopup from './SellingGroupFilterPopup';

import { setShow } from '../../../../../actions/general';
import {
     clearAllSellingGroupFilterSearchParam,
     clearSubscriberSellingGroupFilterOptions,
     clearSubscriberSellingGroupFilterSelectedItems,
     deleteSubscriberSellingGroupFilterSelectedItems,
     setSubscriberSellingGroupFilterOptions,
     setSubscriberSellingGroupFilterSelectedItems,
     setSubscriberSellingGroupFilterSelectedSearch,
} from '../actions';

const getSelectedOptions = (nodes, category, result = []) => {
     nodes.forEach((node) => {
          if (node.checked) {
               // If node has checked children, skip the parent
               if (!node.children?.some((child) => child.checked)) {
                    result.push({
                         value: node.value,
                         label: node.label,
                         category,
                    });
               }
          }
          if (node.children?.length) {
               getSelectedOptions(node.children, category, result);
          }
     });
     return result;
};

const SellingGroupFilter = (props) => {
     const [searchParams, setSearchParams] = useSearchParams();
     const paramSearch = new URLSearchParams(searchParams);

     const dispatch = useDispatch();
     const show = useSelector((state) => state.general.show);
     const sellingGroupFilterOptions = useSelector((state) => state.subscriber.sellingGroupFilterOptions);
     const sellingGroupFilterSelectedSearch = useSelector((state) => state.subscriber.sellingGroupFilterSelected.search);
     const sellingGroupFilterSelectedItems = useSelector((state) => state.subscriber.sellingGroupFilterSelected.items);

     // Init selling group filter selected search
     useEffect(() => {
          const searchFilter = removeTrailingSlash(paramSearch.get('search')).replace(/%2F$/, '');
          if (searchFilter) {
               dispatch(setSubscriberSellingGroupFilterSelectedSearch(searchFilter));
          }
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     // Handle update when selling group filter options changed
     useEffect(() => {
          const selectedFilters = [];

          Object.entries(sellingGroupFilterOptions).forEach(([key, options]) => {
               const selected = getSelectedOptions(options, key);

               if (selected.length > 0) {
                    selectedFilters.push(...selected);
               }
          });

          // No data needs to be changed
          if (!sellingGroupFilterSelectedItems.length && !selectedFilters.length) {
               return;
          }

          dispatch(setSubscriberSellingGroupFilterSelectedItems(selectedFilters));
     }, [sellingGroupFilterOptions]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleClearAllFilter = () => {
          dispatch(clearSubscriberSellingGroupFilterSelectedItems());
          dispatch(clearSubscriberSellingGroupFilterOptions());

          const newSearchParams = dispatch(clearAllSellingGroupFilterSearchParam(paramSearch));
          setSearchParams(newSearchParams);
     };

     const handleChangeSearchSellingGroup = ({ target }) => {
          const value = target.value.replace('/', '');
          if (sellingGroupFilterSelectedSearch === value) return;

          dispatch(setSubscriberSellingGroupFilterSelectedSearch(value));

          if (value) {
               paramSearch.set('search', value);
          } else {
               paramSearch.delete('search');
          }

          const newSearchParams = paramSearch.toString().replace(/%2F$/, '');
          setSearchParams(newSearchParams);
     };

     const handleRemoveTextSearchFilter = () => {
          if (!sellingGroupFilterSelectedSearch) return;

          dispatch(setSubscriberSellingGroupFilterSelectedSearch(''));

          paramSearch.delete('search');
          const newSearchParams = paramSearch.toString().replace(/%2F$/, '');
          setSearchParams(newSearchParams);
     };

     const handleRemoveSelected = (selected) => {
          dispatch(deleteSubscriberSellingGroupFilterSelectedItems(selected));

          // Create a deep copy of the current state to avoid mutation
          const currentGroup = cloneDeep(sellingGroupFilterOptions[selected.category]);

          // Update the state with the new changes
          const checked = false;
          const selectedFilters = {
               ...sellingGroupFilterOptions,
               [selected.category]: updateNodeState(currentGroup, selected.value, checked),
          };

          dispatch(setSubscriberSellingGroupFilterOptions(selectedFilters));

          // Update the URL search params
          paramSearch.delete(selected.category, selected.value);
          paramSearch.delete(selected.category, `${selected.value}/`);

          const newSearchParams = paramSearch.toString().replace(/%2F$/, '');
          setSearchParams(newSearchParams);
     };

     const handleClickButtonFilterMobile = () => {
          dispatch(setShow(POPUP_TYPE.SELLING_GROUP_FILTER_CHOOSE));
     };

     return (
          <div className="selling-group-filter">
               <div className="selling-group-filter-search">
                    <CInputGroup>
                         <CFormInput
                              name="search"
                              type="text"
                              placeholder="Search"
                              className="selling-group-filter-search-input"
                              autoComplete="off"
                              value={sellingGroupFilterSelectedSearch}
                              onChange={handleChangeSearchSellingGroup}
                         />
                    </CInputGroup>
                    <div
                         className={classNames('selling-group-filter-search-icon', { 'cursor-pointer': !!sellingGroupFilterSelectedSearch })}
                         onClick={handleRemoveTextSearchFilter}
                    >
                         {sellingGroupFilterSelectedSearch ? (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                   <g opacity="0.5" clipPath="url(#clip0_216_3320)">
                                        <path
                                             d="M11.4002 2.99967L12.3335 3.93301L8.60016 7.66634L12.3335 11.3997L11.4002 12.333L7.66683 8.59967L3.9335 12.333L3.00016 11.3997L6.7335 7.66634L3.00016 3.93301L3.9335 2.99967L7.66683 6.73301L11.4002 2.99967Z"
                                             fill="currentColor"
                                        />
                                   </g>
                                   <defs>
                                        <clipPath id="clip0_216_3320">
                                             <rect width="16" height="16" fill="white" transform="matrix(-1 0 0 -1 16 16)" />
                                        </clipPath>
                                   </defs>
                              </svg>
                         ) : (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                   <path
                                        d="M14.3333 15.5L9.08333 10.25C8.66667 10.5833 8.1875 10.8472 7.64583 11.0417C7.10417 11.2361 6.52778 11.3333 5.91667 11.3333C4.40278 11.3333 3.12167 10.8089 2.07333 9.76C1.025 8.71111 0.500556 7.43 0.5 5.91667C0.499445 4.40333 1.02389 3.12222 2.07333 2.07333C3.12278 1.02444 4.40389 0.5 5.91667 0.5C7.42944 0.5 8.71083 1.02444 9.76083 2.07333C10.8108 3.12222 11.335 4.40333 11.3333 5.91667C11.3333 6.52778 11.2361 7.10417 11.0417 7.64583C10.8472 8.1875 10.5833 8.66667 10.25 9.08333L15.5 14.3333L14.3333 15.5ZM5.91667 9.66667C6.95833 9.66667 7.84389 9.30222 8.57333 8.57333C9.30278 7.84444 9.66722 6.95889 9.66667 5.91667C9.66611 4.87444 9.30167 3.98917 8.57333 3.26083C7.845 2.5325 6.95944 2.16778 5.91667 2.16667C4.87389 2.16556 3.98861 2.53028 3.26083 3.26083C2.53306 3.99139 2.16833 4.87667 2.16667 5.91667C2.165 6.95667 2.52972 7.84222 3.26083 8.57333C3.99195 9.30444 4.87722 9.66889 5.91667 9.66667Z"
                                        fill="currentColor"
                                   />
                              </svg>
                         )}
                    </div>
               </div>
               <div className="selling-group-filter-selected">
                    <div className="selling-group-filter-selected-filter d-lg-none" onClick={handleClickButtonFilterMobile}>
                         <div className="selling-group-filter-selected-filter-icon">
                              <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                   <path
                                        d="M5.83333 10H9.16667V8.33333H5.83333V10ZM0 0V1.66667H15V0H0ZM2.5 5.83333H12.5V4.16667H2.5V5.83333Z"
                                        fill="currentColor"
                                   />
                              </svg>
                         </div>
                         <span>Filters</span>
                    </div>
                    {sellingGroupFilterSelectedItems.map((selected) => (
                         <div key={`${selected.value}_${selected.label}`} className="selling-group-filter-selected-item">
                              <span>{selected.label}</span>
                              <div className="selling-group-filter-selected-item-icon cursor-pointer" onClick={() => handleRemoveSelected(selected)}>
                                   <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g opacity="0.5" clipPath="url(#clip0_216_3320)">
                                             <path
                                                  d="M11.4002 2.99967L12.3335 3.93301L8.60016 7.66634L12.3335 11.3997L11.4002 12.333L7.66683 8.59967L3.9335 12.333L3.00016 11.3997L6.7335 7.66634L3.00016 3.93301L3.9335 2.99967L7.66683 6.73301L11.4002 2.99967Z"
                                                  fill="currentColor"
                                             />
                                        </g>
                                        <defs>
                                             <clipPath id="clip0_216_3320">
                                                  <rect width="16" height="16" fill="white" transform="matrix(-1 0 0 -1 16 16)" />
                                             </clipPath>
                                        </defs>
                                   </svg>
                              </div>
                         </div>
                    ))}
                    {sellingGroupFilterSelectedItems.length > 0 && (
                         <span className="selling-group-filter-selected-clear" onClick={handleClearAllFilter}>
                              Clear filters
                         </span>
                    )}
               </div>
               <SellingGroupFilterChoose classNameWrap="d-none d-lg-block" />

               {show === POPUP_TYPE.SELLING_GROUP_FILTER_CHOOSE && <SellingGroupFilterPopup handleClearAllFilter={handleClearAllFilter} />}
          </div>
     );
};

SellingGroupFilter.propTypes = {
     handleFilter: PropTypes.func,
};

SellingGroupFilter.defaultProps = {
     handleFilter: DEFAULT_FN,
};

export default memo(SellingGroupFilter);
